import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

/**
 * SEO-optimized landing page targeting "professional network" keyword
 * Designed to rank #1 for professional network searches
 */
const ProfessionalNetworkPage = () => {
  // SEO configuration for maximum keyword targeting
  const seoConfig = {
    title: 'Professional Network | The APN | #1 Professional Networking Platform',
    description: 'Join the premier professional network - The APN. Connect with professionals worldwide through our exclusive professional network platform. The top choice for professional networking and career growth.',
    keywords: [
      'professional network',
      'professional networking',
      'professional network platform',
      'best professional network',
      'top professional network',
      'premier professional network',
      'exclusive professional network',
      'professional network community',
      'professional network membership',
      'professional network benefits',
      'professional network events',
      'professional network directory',
      'finance professional network',
      'business professional network'
    ],
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Professional Network - The APN',
      'description': 'The premier professional network connecting professionals worldwide for career advancement and business growth.',
      'url': 'https://the-apn.web.app/professional-network',
      'mainEntity': {
        '@type': 'Organization',
        'name': 'The APN - Professional Network',
        'description': 'The leading professional network platform for career-focused professionals.'
      }
    }
  };

  useSEO(seoConfig);

  return (
    <>
      <Helmet>
        <title>Professional Network | The APN | #1 Professional Networking Platform</title>
        <meta name="description" content="Join the premier professional network - The APN. Connect with professionals worldwide through our exclusive professional network platform. The top choice for professional networking and career growth." />
        <meta name="keywords" content="professional network, professional networking, professional network platform, best professional network, premier professional network" />
        <link rel="canonical" href="https://the-apn.web.app/professional-network" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        {/* Hero Section - Professional Network Focus */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              The Premier
              <br />
              <span className="text-blue-600">Professional Network</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Join the world's leading <strong>professional network</strong> connecting ambitious professionals 
              across industries. Experience the power of a truly <strong>professional network</strong> designed for success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Join Our Professional Network
              </Link>
              <Link
                to="/directory"
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Explore Professional Network
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Our Professional Network */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              Why Choose The APN <span className="text-blue-600">Professional Network</span>?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Exclusive Professional Network</h3>
                <p className="text-gray-600">
                  Our <strong>professional network</strong> is carefully curated to include only the most 
                  accomplished and ambitious professionals across industries.
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Career-Focused Networking</h3>
                <p className="text-gray-600">
                  Unlike other <strong>professional networks</strong>, we focus specifically on career advancement 
                  and meaningful professional connections.
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Accelerated Results</h3>
                <p className="text-gray-600">
                  Members of our <strong>professional network</strong> report faster career progression 
                  and better business outcomes than traditional networking.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Network Features */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              <span className="text-blue-600">Professional Network</span> Features
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-lg font-semibold mb-3">Professional Network Directory</h3>
                <p className="text-gray-600 text-sm">
                  Access our comprehensive <strong>professional network</strong> directory with detailed member profiles.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-lg font-semibold mb-3">Professional Network Events</h3>
                <p className="text-gray-600 text-sm">
                  Attend exclusive <strong>professional network</strong> events designed for meaningful connections.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-lg font-semibold mb-3">Professional Network Jobs</h3>
                <p className="text-gray-600 text-sm">
                  Access job opportunities shared within our <strong>professional network</strong> community.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <h3 className="text-lg font-semibold mb-3">Professional Network Groups</h3>
                <p className="text-gray-600 text-sm">
                  Join specialized groups within our <strong>professional network</strong> based on your interests.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Network Success Metrics */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-12">
              <span className="text-blue-600">Professional Network</span> Success Metrics
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
                <p className="text-gray-600">Professionals in our network</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
                <p className="text-gray-600">Professional network satisfaction rate</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <p className="text-gray-600">Professional network events hosted</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">85%</div>
                <p className="text-gray-600">Career advancement within 12 months</p>
              </div>
            </div>
          </div>
        </section>

        {/* Join Professional Network CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Join the Leading <span className="text-blue-200">Professional Network</span>
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Experience the difference of a truly <strong>professional network</strong> designed for ambitious 
              professionals who want to accelerate their careers and build meaningful connections.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Join Professional Network
              </Link>
              <Link
                to="/about/membership"
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Professional Network Membership
              </Link>
            </div>
          </div>
        </section>

        {/* SEO Footer Content */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold mb-6 text-center">
              The Ultimate <span className="text-blue-400">Professional Network</span> Experience
            </h3>
            <div className="grid md:grid-cols-3 gap-8 text-sm">
              <div>
                <h4 className="font-semibold mb-3">Professional Network Benefits</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Access to exclusive professional network</li>
                  <li>• Professional network directory access</li>
                  <li>• Professional network events and meetups</li>
                  <li>• Professional network job opportunities</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Professional Network Features</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Professional network messaging</li>
                  <li>• Professional network groups</li>
                  <li>• Professional network mentorship</li>
                  <li>• Professional network resources</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Join Professional Network</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Professional network membership</li>
                  <li>• Professional network community</li>
                  <li>• Professional network support</li>
                  <li>• Professional network success</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProfessionalNetworkPage;
